import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import type { SocialConfigResponse, SocialDashboard } from '../../lib/types';
import { ErrorState, Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The social console and dashboard.
 *
 * Every figure comes from `Interaction`, which is why every reciprocal reward
 * is written onto the row that caused it rather than only onto a counter: a
 * counter answers "how much" and never "between whom", and "between whom" is
 * the only question the abuse section can be built from.
 *
 * The split to read first is real-vs-NPC. Living Trainers act through the same
 * feeding code a player does, so a healthy game has plenty of NPC actions —
 * but if the actions *toward* NPCs dominate, the reduced NPC rate is not
 * reduced enough and players are farming the one target that never runs out.
 */
export function AdminSocialTab() {
  return (
    <div className="stack">
      <DashboardPanel />
      <ConfigPanel />
    </div>
  );
}

function DashboardPanel() {
  const state = useAsync<SocialDashboard>(() => api.get('/admin/social/dashboard', { days: 30 }));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 200 }} />;

  const data = state.data;
  const npcShare =
    data.split.towardNpc + data.split.towardReal > 0
      ? (data.split.towardNpc / (data.split.towardNpc + data.split.towardReal)) * 100
      : 0;

  return (
    <>
      <Panel title="♥ Helping" hint={`last ${data.totals ? 30 : 0} days`}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(126px, 1fr))', gap: 8 }}>
          <Metric label="Feeds / day" value={formatNumber(data.totals.feedsPerDay)} />
          <Metric label="Warms / day" value={formatNumber(data.totals.warmsPerDay)} />
          <Metric label="Active helpers" value={formatNumber(data.totals.activeHelpers)} />
          <Metric label="EXP given / day" value={formatNumber(data.totals.targetExpPerDay)} />
          <Metric label="Team EXP / day" value={formatNumber(data.totals.teamExpPerDay)} />
          <Metric label="Hatch pts / day" value={formatNumber(data.totals.hatchProgressPerDay)} />
          <Metric label="Coins / day" value={formatNumber(data.totals.coinsPerDay)} />
        </div>

        {/* Team EXP against EXP given is the parity check the design is built
            on. Roughly equal means "I help you, we both grow"; far above means
            helping funds itself and the reciprocal rate wants lowering. */}
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          {data.totals.teamExpPerDay > data.totals.targetExpPerDay * 2
            ? 'Reciprocal EXP is more than double what feeding gives its targets — lower the reciprocal rate.'
            : 'Reciprocal EXP is roughly in step with what feeding gives — the loop is paying parity.'}
        </p>
      </Panel>

      <Panel title="🌿 Who is being helped" hint={`${npcShare.toFixed(0)}% toward Living Trainers`}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          <Metric label="By real players" value={formatNumber(data.split.byRealActor)} />
          <Metric label="By Living Trainers" value={formatNumber(data.split.byNpcActor)} />
          <Metric label="Toward real players" value={formatNumber(data.split.towardReal)} />
          <Metric label="Toward Living Trainers" value={formatNumber(data.split.towardNpc)} />
        </div>
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          {npcShare > 50
            ? 'More than half of all help goes to Living Trainers. They never run out, so lower the Living Trainer rate — real players should be the better target.'
            : 'Most help goes to real players, which is what the reduced Living Trainer rate is for.'}
        </p>
      </Panel>

      <Panel title="★ Teams" hint={`${formatNumber(data.teams.slotsFilled)} filled slots`}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          <Metric label="Average Team level" value={data.teams.averageLevel.toFixed(1)} />
          <Metric label="At level 100" value={formatNumber(data.teams.atMaxLevel)} />
        </div>
      </Panel>

      <Panel title="⚠ Pairs worth a look" hint="ranked by reciprocal value traded">
        {/* Reported, never acted on. A couple who play together every evening
            look identical from here to two accounts farming each other, and
            telling them apart is a judgement a human makes with the rest of
            both accounts in front of them. What the game already does
            automatically is make it unrewarding. */}
        {data.suspiciousPairs.length === 0 ? (
          <p className="tiny muted" style={{ margin: 0 }}>Nothing stands out.</p>
        ) : (
          <div className="stack-sm">
            {data.suspiciousPairs.map((pair) => (
              <div key={`${pair.a}|${pair.b}`} className="row row--between tiny">
                <span>
                  {pair.a} ↔ {pair.b}
                </span>
                <span className="muted mono">
                  {formatNumber(pair.interactions)} acts · {formatNumber(pair.reciprocalValue)} value
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="◈ Most interacted Pokémon">
        {data.mostInteracted.length === 0 ? (
          <p className="tiny muted" style={{ margin: 0 }}>Nothing yet.</p>
        ) : (
          <div className="stack-sm">
            {data.mostInteracted.map((row) => (
              <div key={row.pokemonId} className="row row--between tiny">
                <span>
                  {row.name} <span className="muted">Lv {row.level}</span>
                  {row.owner && <span className="muted"> · {row.owner}</span>}
                </span>
                <span className="muted mono">{formatNumber(row.interactions)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

function ConfigPanel() {
  const { push } = useToast();
  const state = useAsync<SocialConfigResponse>(() => api.get('/admin/social/config'));
  const [saving, setSaving] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 300 }} />;

  const groups = [...new Set(state.data.fields.map((field) => field.group))];

  async function save(key: string, value: string | number) {
    setSaving(key);
    try {
      const result = await api.patch<{ key: string; value: number | string }>(
        '/admin/social/config',
        { key, value },
      );
      push('success', `${key} = ${result.value}`);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save that.');
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      {groups.map((group) => (
        <Panel key={group} title={`⚙ ${group}`}>
          <div className="stack-sm">
            {state.data!.fields
              .filter((field) => field.group === group)
              .map((field) => (
                <div key={field.key} className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong className="tiny">{field.label}</strong>
                    <div className="tiny muted">{field.help}</div>
                    {field.value !== field.default && (
                      <div className="tiny muted">default {field.default}</div>
                    )}
                  </span>

                  {/* The one field that is not a number. Rendered from the
                      `options` the server sent rather than from a list here,
                      so a new distribution model appears in the console
                      without a client change — the same rule the labels
                      already follow. */}
                  {field.options ? (
                    <select
                      className="input"
                      style={{ width: 130 }}
                      value={String(field.value)}
                      disabled={saving === field.key}
                      onChange={(event) => void save(field.key, event.target.value)}
                    >
                      {field.options.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      // Keyed on the effective value, so a save the server
                      // clamped remounts showing what actually took effect
                      // rather than the rejected input.
                      key={`${field.key}:${field.value}`}
                      className="input"
                      type="number"
                      style={{ width: 110 }}
                      defaultValue={Number(field.value)}
                      disabled={saving === field.key}
                      onBlur={(event) => {
                        if (Number(event.target.value) !== Number(field.value)) {
                          void save(field.key, Number(event.target.value));
                        }
                      }}
                    />
                  )}
                </div>
              ))}
          </div>
        </Panel>
      ))}
      <p className="tiny muted">
        Values are clamped on save and the field shows what took effect. After changing a rate,
        re-run <code>npm run sim:social -w server</code> — it is what set these defaults and it
        catches the failure that matters: a loop where helping pays back more than it cost.
      </p>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack-sm" style={{ gap: 0 }}>
      <div className="tiny muted">{label}</div>
      <div className="mono">{value}</div>
    </div>
  );
}
